import type { EventLog, EventType } from '@/shared/types';
import { Globe, Tag } from 'lucide-react';
import { haptic } from '@/shared/utils/telegram';
import { formatCurrency } from '@/shared/utils/formatting';

interface EventCardProps {
  event: EventLog;
  onClick: () => void;
}

const getBadgeStyle = (type: EventType) => {
  switch (type) {
    case 'QUALIFIED_CPA':
      return 'bg-amber-600 text-white';
    case 'FTD':
      return 'bg-emerald-500 text-white';
    case 'REG':
      return 'bg-amber-500 text-white';
    case 'REDEP':
      return 'bg-purple-500 text-white';
    case 'DEP':
      return 'bg-amber-500 text-white';
    case 'CLICK':
      return 'bg-blue-500 text-white';
    default:
      return 'bg-gray-500 text-white';
  }
};

const formatEventLabel = (type: EventType): string => {
  return type === 'QUALIFIED_CPA' ? 'CPA' : type;
};

const formatTime = (timestamp?: string | null) => {
  if (!timestamp) return '';
  try {
    return new Date(timestamp).toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  } catch {
    return timestamp;
  }
};

export function EventCard({ event, onClick }: EventCardProps) {
  const handleClick = () => {
    haptic.impact('light');
    onClick();
  };

  return (
    <div
      onClick={handleClick}
      className="group relative flex flex-col p-4 bg-[var(--tg-theme-secondary-bg-color)]/60 backdrop-blur-xl rounded-[18px] active:scale-[0.97] transition-all duration-300 ease-out border border-white/10 dark:border-white/8 shadow-[0_4px_16px_rgba(0,0,0,0.04)] overflow-hidden cursor-pointer"
    >
      {/* Liquid Glass hover effect */}
      <div className="absolute inset-0 bg-gradient-to-br from-white/5 via-transparent to-transparent opacity-0 group-hover:opacity-100 group-active:opacity-50 transition-opacity duration-300" />

      {/* Content */}
      <div className="relative z-10">
        <div className="flex items-center justify-between mb-2.5">
          <div className="flex items-center gap-2.5">
            <span className={`text-[8px] font-black px-2 py-1 rounded-lg uppercase tracking-[0.12em] shadow-sm ${getBadgeStyle(event.event)}`}>
              {formatEventLabel(event.event)}
            </span>
            <span className="text-[13px] font-black leading-none tracking-tight">{event.brand}</span>
          </div>
          <span className="text-[9px] font-bold opacity-40 uppercase tabular-nums tracking-wider">
            {formatTime(event.occurredAt)}
          </span>
        </div>

        <div className="flex items-end justify-between">
          <div className="flex items-center gap-3">
            {event.country && (
              <div className="flex items-center gap-1.5 opacity-70 hover:opacity-100 transition-opacity">
                <Globe size={11} className="text-emerald-500 drop-shadow-sm" />
                <span className="text-[10px] font-black uppercase tracking-wide">{event.country}</span>
              </div>
            )}
            {event.source && (
              <div className="flex items-center gap-1.5 opacity-70 hover:opacity-100 transition-opacity">
                <Tag size={11} className="text-[var(--tg-theme-link-color)] drop-shadow-sm" />
                <span className="text-[10px] font-bold truncate max-w-[80px]">{event.source}</span>
              </div>
            )}
          </div>

          <div className="flex items-center gap-2">
            {event.isDuplicate && (
              <span className="text-amber-500 text-[10px] font-black animate-pulse px-1.5 py-0.5 bg-amber-500/10 rounded-md">DUP</span>
            )}
            {event.amount && parseFloat(event.amount) > 0 && (
              <span className="text-[14px] font-black text-emerald-500 dark:text-emerald-400 tabular-nums tracking-tight drop-shadow-sm">
                +{formatCurrency(parseFloat(event.amount), event.currency || 'EUR')}
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
